import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { TsButtonModule } from '../../../lib/ts-button';
import { TSIconModule } from '../../../lib/ts-icon/ts-icon.module';

export interface GroupByOption {
  key: string;
  label: string;
}

@Component({
  selector: 'ts-group-by',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TsSelectModule, TSIconModule, TsButtonModule],
  template: `
    <div class="w-96 bg-white text-black px-4 space-y-4">
      <div *ngFor="let group of groups(); let i = index; trackBy: trackByIndex" class="flex items-center gap-2">
        <label class="group-label">
          {{ i === 0 ? 'Group By' : 'And' }}
        </label>
        <div class="w-full">
          <ts-select
            [items]="availableOptions(i)"
            itemKey="key"
            itemLabel="label"
            [uiOptions]="uiOptions"
            [(ngModel)]="group.field"
          ></ts-select>
        </div>

        <button (click)="removeGroup(i)" class="text-gray-500 hover:text-red-600 text-lg" data-testid="remove-group-by">
          <ts-icon color="ts-icon-light-gray" icon="ts_trash-alt" />
        </button>
      </div>

      <div class="mt-2">
        <ts-button
          color="basic"
          icon="add"
          (click)="addGroup()"
          [disabled]="groups().length >= options.length"
          data-testid="add-group-by"
          >Add group by</ts-button
        >
      </div>

      <div class="flex justify-between divider">
        <a class="text-disabled-light flex flex-row items-center gap-1 text-black-400">
          <ts-icon color="ts-icon-light-gray" icon="ts_question-circle" />
          Learn about group by
        </a>
        <div class="flex justify-end gap-2">
          <ts-button variant="secondary" (click)="clearGroups()" [disabled]="!canClear()" data-testid="clear-group-by"
            >Clear</ts-button
          >
          <ts-button variant="primary" (click)="apply()" data-testid="apply-group-by">Apply </ts-button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./ts-group-by.component.scss'],
})
export class TsGroupByComponent implements OnInit {
  @Input({ required: true }) options!: GroupByOption[];
  @Input() selectedOptions: string[] = [];
  @Output() groupsChange = new EventEmitter<string[]>();

  uiOptions = {
    roundedBorders: false,
    placeholder: 'Select a field',
    searchPlaceholder: 'Search...',
    size: 'medium',
    disabledClass: null,
  };

  ngOnInit(): void {
    this.selectedOptions?.forEach((field) => {
      this.groups.update((groups) => [...groups, { field }]);
    });
  }

  groups = signal<{ field: string }[]>([]);
  canClear = computed(() => this.groups().length > 0);

  addGroup(): void {
    this.groups.update((groups) => [...groups, { field: '' }]);
  }

  removeGroup(index: number): void {
    this.groups.update((groups) => groups.filter((_, i) => i !== index));
  }

  clearGroups(): void {
    this.groups.set([]);
    this.groupsChange.emit([]);
  }

  apply(): void {
    this.groupsChange.emit(this.groups().map((g) => g.field));
  }

  availableOptions(currentIndex: number): GroupByOption[] {
    const selectedKeys = this.groups()
      .map((g) => g.field)
      .filter((_, i) => i !== currentIndex);
    return this.options.filter((opt) => !selectedKeys.includes(opt.key));
  }

  trackByIndex(index: number): number {
    return index;
  }
}
